/* eslint-disable camelcase */
import React, { useState, useEffect, useRef, forwardRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import _isEmpty from "lodash/isEmpty"
import cx from "classnames"
import PropTypes from "prop-types"
import debounce from "lodash/debounce"
import { useTranslation } from "next-i18next"
import NotifyComponent from "@/components/ProductDetail/v3/ProductDetailView/NotifyComponent"
import NotifySuccess from "@/components/ProductDetail/v3/ProductDetailView/NotifySuccess"

import { getStock, getStockCached } from "@/utils/cart"
import { apim } from "@/constants/api"
import {
  getShortenedUrl,
  sanitizeAndDecodeHtml,
  validateField,
  isValidName,
  isValidPincode,
} from "@/utils/helper"
import {
  addToCompareAnalytics,
  getAnalyticsData,
  quantityOutOfStockAnalytics,
  replaceAddToCartDatalayerPush,
  gtmAddToCartEvent
} from "@/components/ProductDetail/v3/AddToCart/analytics"
import {
  validNumber,
  validPhone,
  validText,
  validateEmail,
} from "@/utils/validate"

import Button from "@/components/core/Button/Button"
import Checkbox from "@/components/core/Checkbox/Checkbox"
import Tooltip from "@/components/core/ToolTip/ToolTip"
import Input from "@/components/core/Input/Input"
import QuantitySelector from "@/components/cart/QuantitySelector"

import { addProduct, removeItems } from "@/store/features/favoritesSlice"
import {
  selectAuthState,
  setAuthModalVisibility,
  setFavoritesLogin,
  setBuyNowLogin,
} from "@/store/features/authSlice"
import { selectGenericState, showToast } from "@/store/features/genericSlice"

import wishIcon from "@/public/icons/wish.svg"
import filledWishIcon from "@/public/icons/wishlist-gray-filled.svg"
import infoIcon from "@/public/icons/warning.svg"
import styles from "@/components/ProductDetail/v3/AddToCart/index.module.scss"
import { selectCartState } from "@/store/features/cartSlice"
import { useRouter } from "next/router"

const AddToCart = forwardRef((props, ref) => {
  const {
    isDiscontinued = false,
    isColorDiscontinued = false,
    productOnStock = 1,
    compareData = {},
    sku = "",
    ProductPriceSpiderIncluded_s = "",
    price = 0,
    backOrder = false,
    isParts = false,
    onQtyChange = () => {},
    staticTexts = {},
    onRemove = () => {},
    onAdd = () => {},
    onAddToCart = () => {},
    isComboSku = false,
    customerBrandName = "",
    ctData = "",
    productData = {},
    productData: {} = {},
    replaceAddToCartCtaLabel = "",
    replaceAddToCartCtaLink = "",
    onAvailableColor = () => {},
    disableEcommerce = false,
    specialOrder = "",
    isRegionSoldOnline = true,
    replaceAddToCart = false,
    linkNewTab = false,
    isEnable = true,
    isFavorite = false,
    disableRatings = false,
    handleNavChange = () => {},
    productCategory = "",
    favItem = [],
    isPdpPropsLoaded = true,
    installError = false,
    masterSku = "",
    availableQty = 0,
    installationData = {},
    isInstallationAdded = false,
    isFragile = false,
    warehouses,
    isBundle,
    productInfo = {},
    installProductInfo = {},
    setPinCodeError,
    pinCodeError,
    installationError,
    setInstallationError,
    error,
    setError,
    loading: installLoading,
    setLoading: setInstallLoading,
    reqItemsError,
    quantity,
    setQuantity,
    handleBuyNow = () => {},
    setPincode: setPincodeValue,
    name,
    desc,
    productInRiverId_s,
    setNotifySuccess,
    notifySuccess,
    notifyContainer,
    isOutOfStock,
    notifyClicked = false,
    imagesData = imagesData,
  } = props

  const { t } = useTranslation("pdp")

  const initState = { phoneNumber: "", name: "", email: "", zipcode: "" }
  const { isAuth, user: { persona = [] } = {} } = useSelector(selectAuthState)
  const { toaster } = useSelector(selectGenericState)
  const { cart } = useSelector(selectCartState)
  const [loading, setLoading] = useState(false)
  const [buyNowLoading, setBuyNowLoading] = useState(false)
  const [isChecked, setIsChecked] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [notifyMeFormData, setNotifyFormData] = useState(initState)
  const [notifyMeErrors, setNotifyFormErrors] = useState({})
  const [leadFormData, setLeadFormData] = useState(initState)
  const [leadFormErrors, setLeadFormErrors] = useState({})
  const [showWelcome, setShowWelcome] = useState(false)
  const [leadGenFlag, setLeadGenFlag] = useState(false)
  const dispatch = useDispatch()
  const [productStock, setProductStock] = useState(1)
  const enableFav = isDiscontinued || isColorDiscontinued ? true : false
  const [showBuyBoxArea, setShowBuyBoxArea] = useState(true)
  const [stockAlert, setStockAlert] = useState({ show: false, mssg: "" })
  const [loginFavouriteCallback, setLoginFavouriteCallback] = useState(null)
  const addToCartButton = useRef(null)
  const localStoreOrShowroomRef = useRef(null)
  const [outOfStockMsg, setOutOfStockMsg] = useState("")
  const [pincode, setPincode] = useState("")
  const [toasterContent, setToasterContent] = useState({
    successMessage: "",
    unfavoriteSuccessMessage: "",
  })

  const isIscUser = persona?.includes("IN_Kohler_ISC")
  const router = useRouter()

  useEffect(() => {
    if (staticTexts?.outOfStockMsg) {
      getShortenedUrl(staticTexts?.outOfStockMsg).then(res =>
        setOutOfStockMsg(res ?? staticTexts?.outOfStockMsg)
      )
    }
  }, [staticTexts.outOfStockMsg])

  useEffect(() => {
    setToasterMessage()
    document.addEventListener("stop-loading-atc", () => {
      setLoading(false)
    })
    document.addEventListener("set-stock-alert", () => {
      setStockAlert({
        show: true,
        mssg: t("kf.pdp.label.noItemsToShip"),
      })
    })
    return () => {
      document.removeEventListener("stop-loading-atc")
      document.removeEventListener("set-stock-alert")
    }
  }, [])

  const element = document.getElementById("product-recs-AT-Similar-items")
  let showSimilarItems = false
  if (element) {
    showSimilarItems = true
  }
  useEffect(() => {
    if (loginFavouriteCallback && isAuth) {
      handleFavorite(loginFavouriteCallback)
    }
  }, [loginFavouriteCallback, isAuth])

  useEffect(() => {
    setProductStock(productOnStock)
  }, [productOnStock])

  useEffect(() => {
    if (installError) {
      setStockAlert({ show: false, mssg: "" })
    }
  }, [installError])

  useEffect(() => {
    if (compareData?.comparedProducts?.find(prod => prod?.compareSku === sku))
      setIsChecked(true)
    else setIsChecked(false)
  }, [compareData?.comparedProducts, sku])

  useEffect(() => {
    setShowBuyBoxArea(!ProductPriceSpiderIncluded_s)
  }, [ProductPriceSpiderIncluded_s, price])

  const getCookieValue = name =>
    document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")?.pop() || ""

  const availablePincode = getCookieValue("pincode")

  useEffect(() => {
    setPincode(availablePincode ?? "")
    setPincodeValue(availablePincode)
  }, [availablePincode])

  const setToasterMessage = async () => {
    if (!_isEmpty(toaster?.content)) {
      const { successMessage, unfavoriteSuccessMessage } = toaster.content
      setToasterContent({
        successMessage: await getShortenedUrl(successMessage),
        unfavoriteSuccessMessage: await getShortenedUrl(
          unfavoriteSuccessMessage
        ),
      })
    }
  }

  const handleChange = async qty => {
    setStockAlert({ show: false, mssg: "" })
    if (backOrder === "Yes" || isParts) {
      setQuantity(qty)
      setLoading(false)
      return
    }
    setLoading(true)

    const cartItem =
      cart.lineItems?.find(item => item.variant.sku === sku) ?? {}
    const cartQuantity = cartItem.quantity ?? 0

    if (qty && qty > 0) {
      setQuantity(qty)
      try {
        const stock =
          isFragile && availableQty > 0
            ? availableQty
            : await getStockCached(sku)

        if (cartQuantity > 0 && stock <= cartQuantity) {
          setStockAlert({
            show: true,
            mssg: t("kf.pdp.label.noItemsToShip"),
          })
        } else if (stock >= qty + cartQuantity) {
          onQtyChange(qty)
        } else {
          setProductStock(stock)
          if (stock > 0) {
            setQuantity(stock - cartQuantity)
            onQtyChange(stock - cartQuantity)

            setStockAlert({
              show: true,
              mssg: t("kf.pdp.label.availableToShip", { qty: stock }),
            })
            quantityOutOfStockAnalytics(productInfo, pincode)
          }
        }
        setLoading(false)
      } catch (err) {
        setLoading(false)
      }
    }
  }

  const navigateToSimilarItems = () => {
    const element = document.getElementById("product-recs-AT-Similar-items")
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      })
    }
  }

  const handleCompare = e => {
    let isCompareChecked = !isChecked
    if (e.target.checked) {
      if (compareData?.comparedProducts?.length < 4) {
        if (
          compareData?.category !== "" &&
          compareData?.category !== productCategory
        ) {
          setErrorMessage(staticTexts?.sameCategory)
          setTimeout(() => {
            setErrorMessage("")
          }, 2000)
          isCompareChecked = false
          onRemove(sku)
        } else {
          onAdd(sku)
        }
      } else {
        setErrorMessage(staticTexts?.maxProduct)
        setTimeout(() => {
          setErrorMessage("")
        }, 2000)
        isCompareChecked = false
        onRemove(sku)
      }
    } else {
      onRemove(sku)
    }
    setIsChecked(isCompareChecked)

    const page = window.adobeDataLayer.getState("page")

    window.adobeDataLayer?.push({
      event: "cmp:click",
      eventInfo: JSON.parse(addToCompareAnalytics(!isCompareChecked)),
      productInfo,
      page,
    })
  }

  const handleATCKeydown = e => {
    if (e.keyCode === 13 || e.keyCode === 32) {
      window.previewCartKeySource = true
    } else {
      window.previewCartKeySource = false
    }
  }

  const addToCartWrapper = (actions, isBuyNow = false) => {
    isBuyNow ? handleBuyNow(actions) : onAddToCart(actions)
  }

  const handleAddToCart = (isBuyNow = false) => {
    isBuyNow ? setBuyNowLoading(true) : setLoading(true)
    if (!pincode) {
      setPinCodeError(true)
      setLoading(false)
      setBuyNowLoading(false)
      return
    }
    if (pinCodeError) {
      setLoading(false)
      setBuyNowLoading(false)
      return
    }
    if (error || installationError) {
      setLoading(false)
      setBuyNowLoading(false)
      return
    }
    if (installLoading) {
      setLoading(false)
      setBuyNowLoading(false)
      return
    }
    setStockAlert({ show: false, mssg: "" })
    if (backOrder === "Yes" || isParts) {
      addToCartWrapper(
        [
          {
            sku: sku,
            quantity: quantity,
            isBundle: isComboSku,
            backorder: true,
            brand: customerBrandName?.toLowerCase(),
            warehouses: warehouses,
          },
        ],
        isBuyNow
      )
      return
    }
    if (addToCartButton.current)
      window.cartButtonElement = addToCartButton.current.firstElementChild
    const cartItem =
      cart.lineItems?.find(item => item.variant.sku === sku) ?? {}
    const cartQuantity = cartItem.quantity ?? 0
    let custom = cartItem?.custom ?? {}

    getStock(sku).then(stock => {
      setProductStock(stock)
      if (cartQuantity + quantity > stock) {
        if (stock > cartQuantity) {
          addToCartWrapper(
            [
              {
                sku: sku,
                quantity: stock - cartQuantity,
                brand: customerBrandName?.toLowerCase(),
                warehouses: warehouses,
                custom,
              },
            ],
            isBuyNow
          )
          setStockAlert({
            show: true,
            mssg: t("kf.pdp.label.availableToShip", { qty: stock }),
          })
        } else {
          setStockAlert({
            show: true,
            mssg: t("kf.pdp.label.noItemsToShip"),
          })
        }
        quantityOutOfStockAnalytics(productInfo, pincode)

        setLoading(false)
        setBuyNowLoading(false)
      } else {
        if (installationData?.isInstallation && isInstallationAdded) {
          const payload = [
            {
              sku: sku,
              quantity: quantity,
              isBundle: isBundle,
              brand: customerBrandName?.toLowerCase(),
              isInstallService: true,
              installServiceName: installationData?.sku,
              installServiceQty: quantity,
              warehouses: warehouses,
              custom,
            },
          ]

          if (!isBundle) {
            if (Object.keys(cartItem).length === 0) {
              custom = {
                fields: { associatedProduct: sku, warehouses: warehouses },
              }
            } else {
              custom = {
                fields: { associatedProduct: sku },
              }
            }
            payload.push({
              sku: installationData?.sku,
              quantity: quantity,
              isBundle: false,
              brand: customerBrandName?.toLowerCase(),
              warehouses: warehouses,
              custom,
            })
          }

          addToCartWrapper(payload, isBuyNow)
        } else {
          addToCartWrapper(
            [
              {
                sku: sku,
                quantity: quantity,
                isBundle: isBundle,
                brand: customerBrandName?.toLowerCase(),
                warehouses: warehouses,
                custom,
              },
            ],
            isBuyNow
          )
        }
      }
    })

    const page = window.adobeDataLayer.getState("page")
    const eventInfo = getAnalyticsData(
      isBuyNow ? "buy now" : staticTexts.addToCart,
      "",
      true
    )
    const productArray = [productInfo]
    if (isInstallationAdded && !_isEmpty(installProductInfo)) {
      productArray.push(installProductInfo)
    }
    const { ...rest } = router.query
    const utmPayload={}
    const utmParams = [
      "utm_source",
      "utm_medium",
    ]
    utmParams?.forEach(param => {
      if (rest?.[param]) {
        utmPayload[param] = decodeURIComponent(rest[param])
      }
      else{
        utmPayload[param]=""
      }
    })
    gtmAddToCartEvent({productInfo, utmPayload})

    window.adobeDataLayer?.push({
      event: "cmp:click",
      eventInfo,
      page,
      productArray,
    })
  }

  const handleNotification = () => {
    const { name, email, phoneNumber, zipcode } = notifyMeFormData
    const {
      price,
      productData: { name: skuName, desc: skuDescription },
    } = props
    const isValid = Object?.values(notifyMeErrors)?.every(
      el => el === null || el === ""
    )
    if (isValid) {
      const body = {
        mastervariantkey: masterSku,
        sku: sku,
        skuName: skuName,
        skuDescription: skuDescription,
        skuPrice: `${price}`,
        customer: {
          name: name,
          email: email,
          phone: phoneNumber,
        },
        pincode: zipcode,
        qty: "1",
        pdpUrl: window.location.href,
      }
      apim
        .post("catalog/product/oos/notify", body)
        .then(res => {
          if (res) {
            setShowWelcome(true)
          }
        })
        .catch(error => {
          setNotifyFormErrors({
            ...notifyMeErrors,
            zipcode: error?.response?.data?.result?.error,
          })
        })
    }
  }

  const handleCreateLead = () => {
    const { name, email, phoneNumber, zipcode } = leadFormData
    const isValid = Object?.values(leadFormErrors)?.every(
      el => el === null || el === ""
    )
    if (isValid) {
      let body = {
        email: email,
        contactNumber: phoneNumber,
        name: name,
        pincode: zipcode,
        consent: true,
        sku: sku,
        inRiver: masterSku,
        productName: productData.name,
      }
      apim
        .post("support/lead/create", body)
        .then(res => {
          if (res) {
            setLeadGenFlag(true)
          }
        })
        .catch(error => {
          setLeadFormErrors({
            ...leadFormErrors,
            zipcode: error?.result?.error,
          })
        })
    }
  }

  const checkDisabled = data => {
    const { name, email, zipcode } = data
    return !(name && email && zipcode)
  }

  const onFieldChange = e => {
    const { name = "", value = "" } = e.target
    handleFieldChange(value, name, notifyMeFormData, setNotifyFormData)
    setNotifyFormErrors({ ...notifyMeErrors, [name]: "" })
  }

  const onLeadFieldChange = e => {
    const { name = "", value = "" } = e.target
    handleFieldChange(value, name, leadFormData, setLeadFormData)
    setLeadFormErrors({ ...leadFormErrors, [name]: "" })
  }

  const handleFieldChange = (
    val = "",
    fieldName = "",
    data = {},
    setData = () => {}
  ) => {
    const inputValue = val.trimStart()
    if (inputValue === "") {
      setData({
        ...data,
        [fieldName]: inputValue,
      })
    } else if (fieldName === "phoneNumber") {
      if (validNumber(inputValue) && inputValue.length <= 10) {
        setData({
          ...data,
          [fieldName]: inputValue,
        })
      }
    } else if (fieldName === "name") {
      if (validText(inputValue)) {
        setData({
          ...data,
          [fieldName]: inputValue,
        })
      }
    } else if (fieldName === "zipcode") {
      if (validNumber(inputValue) && inputValue.length <= 6) {
        setData({
          ...data,
          [fieldName]: inputValue,
        })
      }
    } else if (fieldName === "email") {
      setData({
        ...data,
        [fieldName]: inputValue.trim(),
      })
    }
  }

  const onFieldBlur = e => {
    const { name = "", value = "" } = e.target
    handleFieldBlur(value, name, notifyMeErrors, setNotifyFormErrors)
  }

  const onLeadFieldBlur = e => {
    const { name = "", value = "" } = e.target
    handleFieldBlur(value, name, leadFormErrors, setLeadFormErrors)
  }

  const handleFieldBlur = (
    val = "",
    fieldName = "",
    errors,
    setError = () => {}
  ) => {
    const inputValue = val.trimStart()
    const { validEmail, validPincode, validName, validPhoneNumber } =
      staticTexts
    if (fieldName === "phoneNumber") {
      if (inputValue && !validPhone(inputValue)) {
        setError({
          ...errors,
          [fieldName]: validPhoneNumber,
        })
      }
    } else if (fieldName === "name") {
      if (!inputValue) {
        setError({
          ...errors,
          [fieldName]: validName,
        })
      }
    } else if (fieldName === "zipcode") {
      if (inputValue.length < 6) {
        setError({
          ...errors,
          [fieldName]: validPincode,
        })
      }
    } else if (fieldName === "email") {
      if (!validateEmail(inputValue)) {
        setError({
          ...errors,
          [fieldName]: validEmail,
        })
      }
    }
  }

  const handleLogin = (e, isBuyNow = false) => {
    e.preventDefault()
    e.stopPropagation()
    if (isBuyNow) {
      dispatch(setBuyNowLogin())
      window.loginCallback = () => {
        handleAddToCart(true)
      }
    } else {
      window.loginFavouriteCallback = () => {
        setLoginFavouriteCallback(e)
      }
      dispatch(setFavoritesLogin())
    }
    dispatch(setAuthModalVisibility({ show: true }))
  }

  const handleFavorite = e => {
    e.preventDefault()
    addFavoriteAnalytics(isFavorite)
    if (isFavorite === true) {
      favItem?.map(fav => {
        if (fav.productId === ctData && isAuth) {
          dispatch(removeItems([fav.id]))
            .unwrap()
            .then(() => {
              dispatch(
                showToast({
                  message:
                    toasterContent.unfavoriteSuccessMessage ||
                    staticTexts.removeItem,
                  isVisible: true,
                })
              )
            })
            .catch(err => {
              dispatch(
                showToast({
                  message: staticTexts.removeItemError,
                  isVisible: true,
                })
              )
            })
        }
      })
    } else {
      dispatch(addProduct(sku))
        .unwrap()
        .then(() => {
          dispatch(
            showToast({
              message:
                toasterContent.successMessage || staticTexts.favSuccessMsg,
              isVisible: true,
            })
          )
        })
        .catch(err => {
          dispatch(
            showToast({
              message: staticTexts.favErrorMsg,
              isVisible: true,
            })
          )
        })
    }
    window.keyFocusCallBack = e.target.id
  }

  const addFavoriteAnalytics = checked => {
    const eventInfo = {
      eventAction: `pdp:product details:${
        checked ? "remove" : "add"
      } favorites`,
      eventName: `pdp:product details:${checked ? "remove" : "add"} favorites`,
      eventType: "navigation",
      internalLinkName: `${checked ? "remove" : "add"} favorites`,
      internalLinkPosition: "pdp",
      internalLinkType: "pdp:favorites",
      internalLinkZoneName: "pdp:product details",
      internalLinkURL: "n/a",
      clickInternalLinks: "true",
    }

    const page = window.adobeDataLayer.getState("page")

    window.adobeDataLayer?.push({
      event: "cmp:click",
      eventInfo,
      productInfo,
      page,
    })
  }

  const productAnalyticsData = productData || {}

  useEffect(() => {
    setTimeout(() => {
      addDatalayerForLocalStoreOrShowroom()
    }, 500)
  }, [])

  const addDatalayerForLocalStoreOrShowroom = () => {
    if (localStoreOrShowroomRef.current) {
      const localStoreOrShowroomAnchorLink =
        localStoreOrShowroomRef.current.querySelector("a")
      if (localStoreOrShowroomAnchorLink) {
        localStoreOrShowroomAnchorLink.classList.add("gbh-data-layer")
        localStoreOrShowroomAnchorLink.setAttribute(
          "data-gbh-data-layer",
          getAnalyticsData(
            "local store or showroom",
            localStoreOrShowroomAnchorLink.getAttribute("href")
          )
        )
        localStoreOrShowroomAnchorLink.setAttribute(
          "data-gbh-data-layer-custom",
          productAnalyticsData.stringProductInfo
        )
      }
    }
  }

  const AddToCartActionsMarkup = () => {
    return (
      <div className="add-cart-container">
        <div className="quantity-selector-container">
          <QuantitySelector
            productData={productAnalyticsData}
            value={quantity}
            onChange={handleChange}
            loading={loading || installError}
            isPDPQuantitySelector={true}
            doNotRequiredAnalyticsData={true}
            isAddToCart={true}
            onLoading={setLoading}
            isError={installError}
            setQuantity={setQuantity}
          />
        </div>
        <div className="add-to-cart-container" ref={addToCartButton}>
          <Button
            role="button"
            ariaLabel={staticTexts.ariaLabel.addProductToCart}
            label={staticTexts.addToCart}
            onClick={handleAddToCart}
            onKeyDown={handleATCKeydown}
            size="large"
            type="primary"
            flexible={true}
            disabled={
              loading ||
              !quantity ||
              parseInt(quantity) < 1 ||
              !price ||
              reqItemsError
            }
            loading={loading}
          />
        </div>
      </div>
    )
  }

  const divRef = !isOutOfStock ? { ref: ref } : {}
  return (
    <div className={styles.addToCartWrapper} {...divRef}>
      <div className="row no-gutters buy-area">
        <div
          className={`add-to-cart col-12 ${
            isDiscontinued ? "add-to-cart__discontinued" : ""
          }`}
        >
          {showBuyBoxArea
            ? !isDiscontinued &&
              (!(productStock > 0) && !disableEcommerce ? (
                specialOrder === "Yes" || backOrder === "Yes" || isParts ? (
                  <>
                    {stockAlert.show && (
                      <div className="product-detail-page__stock-alert">
                        <img alt="" src={infoIcon.src} />
                        <span>{stockAlert.mssg}</span>
                      </div>
                    )}
                    {isRegionSoldOnline && (
                      <>
                        {" "}
                        <div className="add-cart-container">
                          <div className="quantity-selector-container">
                            <QuantitySelector
                              productData={productAnalyticsData}
                              value={quantity}
                              onChange={handleChange}
                              loading={loading}
                              isPDPQuantitySelector={true}
                              doNotRequiredAnalyticsData={true}
                              isAddToCart={true}
                              onLoading={setLoading}
                              isError={installError}
                              setQuantity={setQuantity}
                            />
                          </div>
                          <div
                            className="add-to-cart-container"
                            ref={addToCartButton}
                          >
                            <Button
                              role="button"
                              ariaLabel={staticTexts.ariaLabel.addProductToCart}
                              label={staticTexts.addToCart}
                              onClick={handleAddToCart}
                              onKeyDown={handleATCKeydown}
                              size="large"
                              type="primary"
                              flexible={true}
                              disabled={
                                loading ||
                                !quantity ||
                                parseInt(quantity) < 1 ||
                                !price ||
                                reqItemsError
                              }
                              loading={loading}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </>
                ) : isPdpPropsLoaded && isRegionSoldOnline ? (
                  <div className="out-of-stock-container">
                    {!showWelcome ? (
                      <>
                        <div
                          className={`${
                            showSimilarItems ? "out-of-stock-div" : ""
                          }`}
                        >
                          <span
                            className={`out-of-stock ${
                              showSimilarItems
                                ? "out-of-stock__similar-items"
                                : ""
                            }`}
                          >
                            {staticTexts.outOfStock}
                          </span>

                          {showSimilarItems ? (
                            <span
                              className="view-similar"
                              onClick={navigateToSimilarItems}
                            >
                              View Similar Items
                            </span>
                          ) : null}
                        </div>
                        {outOfStockMsg ? (
                          <span
                            className="out-of-stock-description"
                            ref={localStoreOrShowroomRef}
                          >
                            {sanitizeAndDecodeHtml(outOfStockMsg)}
                          </span>
                        ) : (
                          <span className="out-of-stock-description">
                            {staticTexts.notifyEmailOosTxt}
                          </span>
                        )}
                        <NotifyComponent
                          staticTexts={staticTexts}
                          productAnalyticsData={productAnalyticsData}
                          productInRiverId_s={masterSku}
                          name={props?.skuName}
                          desc={props?.skuDescription}
                          sku={sku}
                          price={price}
                          setNotifySuccess={setShowWelcome}
                          fromAddToCart={true}
                          notifyContainer={notifyContainer}
                          notifyClicked={notifyClicked}
                          productCategory={productCategory}
                          imagesData={imagesData}
                        />
                      </>
                    ) : (
                      <NotifySuccess staticTexts={staticTexts} />
                    )}
                  </div>
                ) : (
                  <div className="out-of-stock-container">
                    <>
                      {isRegionSoldOnline === false ? (
                        showWelcome === false ? (
                          <>
                            <div
                              className={`${
                                showSimilarItems ? "out-of-stock-div" : ""
                              }`}
                            >
                              <span
                                className={`out-of-stock ${
                                  showSimilarItems
                                    ? "out-of-stock__similar-items"
                                    : ""
                                }`}
                              >
                                {staticTexts.outOfStock}
                              </span>
                              {showSimilarItems ? (
                                <span
                                  className="view-similar"
                                  onClick={navigateToSimilarItems}
                                >
                                  View Similar Items
                                </span>
                              ) : null}
                            </div>
                            {outOfStockMsg ? (
                              <span
                                className="out-of-stock-description"
                                ref={localStoreOrShowroomRef}
                              >
                                {sanitizeAndDecodeHtml(outOfStockMsg)}
                              </span>
                            ) : (
                              <span className="out-of-stock-description">
                                {staticTexts.notifyEmailOosTxt}
                              </span>
                            )}
                            <NotifyComponent
                              staticTexts={staticTexts}
                              productAnalyticsData={productAnalyticsData}
                              productInRiverId_s={masterSku}
                              name={props?.skuName}
                              desc={props?.skuDescription}
                              sku={sku}
                              price={price}
                              setNotifySuccess={setShowWelcome}
                              notifyContainer={notifyContainer}
                              notifyClicked={notifyClicked}
                              fromAddToCart={true}
                              productCategory={productCategory}
                              imagesData={imagesData}
                            />
                          </>
                        ) : (
                          <NotifySuccess staticTexts={staticTexts} />
                        )
                      ) : (
                        <>
                          {leadGenFlag ? (
                            <div className="out-of-stock__success-message lead_success_message">
                              <p className="out-of-stock__thank-title">
                                {staticTexts.leadSuccessTxt}
                              </p>
                              <p className="out-of-stock__thank-description">
                                {sanitizeAndDecodeHtml(
                                  staticTexts.leadSuccessMsg
                                )}
                              </p>
                            </div>
                          ) : (
                            <>
                              <span className="out-of-stock">
                                {staticTexts.nonSellable}
                              </span>
                              <span className="out-of-stock-description">
                                {sanitizeAndDecodeHtml(
                                  staticTexts.notifyEmailOosTxt
                                )}
                              </span>
                              <NotifyComponent
                                staticTexts={staticTexts}
                                productAnalyticsData={productAnalyticsData}
                                productInRiverId_s={masterSku}
                                name={props?.skuName}
                                desc={props?.skuDescription}
                                sku={sku}
                                price={price}
                                setNotifySuccess={setShowWelcome}
                                fromAddToCart={true}
                                notifyContainer={notifyContainer}
                                notifyClicked={notifyClicked}
                                productCategory={productCategory}
                                imagesData={imagesData}
                              />
                            </>
                          )}
                        </>
                      )}
                    </>
                  </div>
                )
              ) : (
                <>
                  {stockAlert.show && (
                    <div className="product-detail-page__stock-alert">
                      <img alt="" src={infoIcon?.src} />
                      <span>{stockAlert.mssg}</span>
                    </div>
                  )}

                  {isRegionSoldOnline !== false ? (
                    error !==
                      "Currently, this product is out of stock for your location" && (
                      <>
                        <div
                          className={`add-cart-container ${
                            isIscUser ? "two-elem" : "three-elem"
                          }`}
                        >
                          <div className="quantity-selector-container">
                            <QuantitySelector
                              productData={productAnalyticsData}
                              value={quantity}
                              onChange={handleChange}
                              loading={loading}
                              isPDPQuantitySelector={true}
                              doNotRequiredAnalyticsData={true}
                              isAddToCart={true}
                              onLoading={setLoading}
                              isError={installError}
                              setQuantity={setQuantity}
                            />
                          </div>
                          <div
                            className="add-to-cart-container"
                            ref={addToCartButton}
                          >
                            <Button
                              role="button"
                              ariaLabel={staticTexts.ariaLabel.addProductToCart}
                              label={staticTexts.addToCart}
                              onClick={() => handleAddToCart(false)}
                              onKeyDown={handleATCKeydown}
                              size="large"
                              type="primary"
                              flexible={true}
                              disabled={
                                loading ||
                                !quantity ||
                                parseInt(quantity) < 1 ||
                                !price ||
                                reqItemsError
                              }
                              loading={loading}
                            />
                          </div>
                          {!isIscUser ? (
                            <div className="buy-now-container">
                              <Button
                                role="button"
                                ariaLabel="buy now"
                                label="Buy Now"
                                onClick={e => {
                                if (isAuth) {
                                  handleAddToCart(true)
                                } else {
                                  if (!pincode) {
                                    setPinCodeError(true)
                                    setLoading(false)
                                    setBuyNowLoading(false)
                                    return
                                  } else {
                                    handleLogin(e, true)
                                  }
                                }
                              }}
                                size="large"
                                type="ghost"
                                flexible={true}
                                disabled={
                                  loading ||
                                  !quantity ||
                                  parseInt(quantity) < 1 ||
                                  !price ||
                                  reqItemsError
                                }
                              />
                            </div>
                          ) : null}
                        </div>
                      </>
                    )
                  ) : (
                    <>
                      {leadGenFlag ? (
                        <div className="out-of-stock__success-message lead_success_message">
                          <p className="out-of-stock__thank-title">
                            {staticTexts.leadSuccessTxt}
                          </p>
                          <p className="out-of-stock__thank-description">
                            {sanitizeAndDecodeHtml(staticTexts.leadSuccessMsg)}
                          </p>
                        </div>
                      ) : (
                        <>
                          <span className="out-of-stock">
                            {staticTexts.nonSellable}
                          </span>
                          <span className="out-of-stock-description">
                            {sanitizeAndDecodeHtml(
                              staticTexts.notifyEmailOosTxt
                            )}
                          </span>
                          <NotifyComponent
                            staticTexts={staticTexts}
                            productAnalyticsData={productAnalyticsData}
                            productInRiverId_s={masterSku}
                            name={props?.skuName}
                            desc={props?.skuDescription}
                            sku={sku}
                            price={price}
                            setNotifySuccess={setShowWelcome}
                            fromAddToCart={true}
                            notifyContainer={notifyContainer}
                            notifyClicked={notifyClicked}
                            productCategory={productCategory}
                            imagesData={imagesData}
                          />
                        </>
                      )}
                    </>
                  )}
                  {error ===
                    "Currently, this product is out of stock for your location" &&
                  !notifySuccess ? (
                    <NotifyComponent
                      staticTexts={staticTexts}
                      setNotifySuccess={setNotifySuccess}
                      productAnalyticsData={productAnalyticsData}
                      productInRiverId_s={productInRiverId_s}
                      name={name}
                      desc={desc}
                      sku={sku}
                      price={price}
                      notifyContainer={notifyContainer}
                      notifyClicked={notifyClicked}
                      productCategory={productCategory}
                      imagesData={imagesData}
                    />
                  ) : null}
                  {error ===
                    "Currently, this product is out of stock for your location" &&
                  notifySuccess ? (
                    <NotifySuccess staticTexts={staticTexts} />
                  ) : null}
                </>
              ))
            : null}
          {replaceAddToCart && !isDiscontinued ? (
            <div className="replace-add-to-cart-container">
              <Button
                onClick={() =>
                  replaceAddToCartDatalayerPush(
                    replaceAddToCartCtaLabel,
                    replaceAddToCartCtaLink
                  )
                }
                role="button"
                ariaLabel={replaceAddToCartCtaLabel}
                label={replaceAddToCartCtaLabel}
                size="large"
                type="primary"
                flexible={true}
                to={replaceAddToCartCtaLink}
                target={linkNewTab ? "_blank" : "_self"}
              />
            </div>
          ) : null}

          {!isDiscontinued && isRegionSoldOnline ? (
            <div className="bottom-section">
              {/* {!isBundle && (
                <div className="add-to-compare">
                  <Checkbox
                    // analyticsData={addToCompareAnalytics(isChecked)}
                    // analyticsDataCustom={productAnalyticsData.stringProductInfo}
                    value={staticTexts.compareTxt}
                    checked={isChecked}
                    onChange={e => handleCompare(e)}
                    // disabled={isEnable}
                  />
                  {errorMessage !== "" && <Tooltip message={errorMessage} />}
                </div>
              )} */}
              {!isBundle && (
                <div
                  className={cx({
                    favorites: true,
                    "favorites--discontinued": enableFav,
                  })}
                  id="kf-pdp-favorites"
                  onClick={isAuth ? handleFavorite : handleLogin}
                  role="button"
                  tabIndex="0"
                  aria-label={staticTexts.favTxt}
                  data-gbh-clickable="true"
                >
                  <img
                    src={isFavorite ? filledWishIcon.src : wishIcon.src}
                    alt={staticTexts.favTxt}
                  />
                  <span className="text">{staticTexts.favTxt} </span>
                </div>
              )}
              {!isDiscontinued && !disableRatings ? (
                <div
                  data-bv-show="rating_summary"
                  data-bv-product-id={sku}
                  className="product-detail-page__inline-rating"
                  onClick={() => handleNavChange(4, true)}
                ></div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
})

AddToCart.propTypes = {
  staticTexts: PropTypes.object,
}

AddToCart.displayName = "AddToCart"

export default AddToCart
